// client/src/components/pages/LexiStar/DispatchBoard/AbsorptionRing.tsx (ST-00250)
//
// The absorption ring of an order card: an SVG ring filled to the share of the target absorbed
// (capped at a full ring), the percentage in the middle, the state's word and glyph under it. The
// colour follows the state (boardLogic.STATE_META: blue under, green on, orange over), always with
// the word, so the state reads without colour. Announced as one sentence.

import type { Absorption } from '../../../../types/lexistar-dispatch-board';
import { STATE_META } from './boardLogic';

export default function AbsorptionRing({ absorption, size = 96 }: { absorption: Absorption; size?: number }) {
  const meta = STATE_META[absorption.state];
  const stroke = Math.max(6, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const share = absorption.ratio == null ? 0 : Math.min(1, Math.max(0, absorption.ratio));
  const pctText = absorption.pct == null ? '—' : `${absorption.pct}%`;
  const label = absorption.pct == null ? `${meta.word}` : `Absorption ${absorption.pct} percent, ${meta.word.toLowerCase()}`;
  return (
    <div className="flex flex-col items-center gap-1" data-testid="absorption-ring" data-state={absorption.state}>
      <svg
        role="img"
        aria-label={label}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="block"
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-slate-200" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className={`${meta.ring} transition-[stroke-dashoffset] duration-300`}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - share)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="central"
          textAnchor="middle"
          className={`fill-current ${meta.text} font-bold tabular-nums`}
          style={{ fontSize: size * 0.26 }}
        >
          {pctText}
        </text>
      </svg>
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${meta.pill}`}>
        <span aria-hidden="true">{meta.glyph}</span>
        {meta.word}
      </span>
    </div>
  );
}
